import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import RoomSelection from './components/RoomSelection';
import CandidateView from './components/CandidateView';
import InterviewerView from './components/InterviewerView';
import HRSignupPage from './components/HRSignupPage';
import HRLoginPage from './components/HRLoginPage';
import CalendarView from './components/CalendarView';
import WelcomePage from './components/WelcomePage';
import ResultsView from './components/ResultsView';
import AIInterviewView from './components/AIInterviewView';
import TeamManagementView from './components/TeamManagementView';
import HowItWorksPage from './components/HowItWorksPage';
import FeaturesPage from './components/FeaturesPage';
import ContactPage from './components/ContactPage';
import PrivacyPage from './components/PrivacyPage';
import AnalyticsTracker from './components/AnalyticsTracker';
import SuperadminLoginPage from './components/SuperadminLoginPage';
import SuperadminDashboard from './components/SuperadminDashboard';
import HRDashboard from './components/hr/HRDashboard';
import ManagerDashboard from './components/hr/ManagerDashboard';
import BUDashboard from './components/hr/BUDashboard';
import InterviewerDashboard from './components/hr/InterviewerView';
//import './styles/global.css';

import './styles/global.css';
const getStoredUser = () => {
  try {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const authData = params.get('auth_data');
      if (authData) {
        try {
          const cleanB64 = authData.replace(/ /g, '+');
          let decodedJson = '';
          try {
            decodedJson = decodeURIComponent(escape(window.atob(cleanB64)));
          } catch (e1) {
            decodedJson = window.atob(cleanB64);
          }
          const decodedUser = JSON.parse(decodedJson);
          if (decodedUser && decodedUser.id) {
            localStorage.setItem('user', JSON.stringify(decodedUser));
            if (decodedUser.token) {
              localStorage.setItem('token', decodedUser.token);
            }
            // Remove auth_data from URL without triggering reload
            const newUrl = window.location.pathname + window.location.search.replace(/[?&]auth_data=[^&]+/, '').replace(/^&/, '?').replace(/\?$/, '');
            window.history.replaceState({}, document.title, newUrl);
            return decodedUser;
          }
        } catch (e) {
          console.error('Failed to parse auth_data from URL', e);
        }
      }
    }
    const rawUser = localStorage.getItem('user');
    if (!rawUser) return null;
    let parsed = null;
    try {
      parsed = JSON.parse(rawUser);
    } catch (e) {
      return null;
    }
    if (!parsed || typeof parsed !== 'object') return null;
    
    // Automatically clean up stale superadmin sessions from standard user key
    if (parsed.user_type === 'SUPERADMIN') {
      localStorage.removeItem('user');
      return null;
    }
    
    return parsed;
  } catch (error) {
    return null;
  }
};

const isAuthenticatedUser = () => {
  const user = getStoredUser();
  return Boolean(user && user.id && user.user_type !== 'SUPERADMIN');
};

// Wrapper for CandidateView to extract roomId from URL
const CandidateViewWrapper = () => {
  const { roomId } = useParams();
  const user = getStoredUser();
  const userName = user?.full_name || user?.name || '';
  return <CandidateView roomId={roomId} userName={userName} />;
};

// Wrapper for InterviewerView
const InterviewerViewWrapper = () => {
  const { roomId } = useParams();
  const user = getStoredUser();
  const userName = user?.full_name || user?.name || 'Interviewer';
  return <InterviewerView roomId={roomId} userName={userName} />;
};

// Auth Guard Component - Controls access based on login status
const AuthGuard = ({ children }) => {
  const isAuthenticated = isAuthenticatedUser();

  // If not authenticated, redirect to login page
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If authenticated, render children (RoomSelection or other protected routes)
  return children;
};

// HR Admin Guard Component - Controls access for HR admins specifically
const HRGuard = ({ children }) => {
  const user = getStoredUser();
  const isAuthenticated = Boolean(user && user.id);
  const isHR = user && (user.user_type === 'HR' || user.role === 'HR');

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isHR) {
    return <Navigate to="/interview" replace />;
  }

  return children;
};

// Manager Guard Component - Controls access for project managers
const ManagerGuard = ({ children }) => {
  const user = getStoredUser();
  const isAuthenticated = Boolean(user && user.id);
  const isManager = user && (user.user_type === 'MANAGER' || user.role === 'MANAGER');

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isManager) {
    return <Navigate to="/interview" replace />;
  }

  return children;
};

// BU Head Guard Component - Controls access for Business Unit heads
const BUHeadGuard = ({ children }) => {
  const user = getStoredUser();
  const isAuthenticated = Boolean(user && user.id);
  const isBU = user && (user.user_type === 'BU_HEAD' || user.role === 'BU_HEAD');

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isBU) {
    return <Navigate to="/interview" replace />;
  }

  return children;
};

const SuperadminGuard = ({ children }) => {
  const rawSuperadmin = localStorage.getItem('superadmin_user');
  let user = null;
  try {
    user = rawSuperadmin ? JSON.parse(rawSuperadmin) : null;
  } catch (e) {
    user = null;
  }
  const isAuthenticated = Boolean(user && user.id);
  const isSuperadmin = user && user.user_type === 'SUPERADMIN';

  if (!isAuthenticated || !isSuperadmin) {
    return <Navigate to="/superadmin/login" replace />;
  }

  return children;
};

const PublicOnlyRoute = ({ children }) => {
  if (isAuthenticatedUser()) {
    const user = getStoredUser();
    const role = user?.user_type || user?.role;
    if (role === 'BU_HEAD') return <Navigate to="/bu-dashboard" replace />;
    if (role === 'MANAGER') return <Navigate to="/manager-dashboard" replace />;
    if (role === 'INTERVIEWER') return <Navigate to="/interviewer-dashboard" replace />;
    if (role === 'HR') return <Navigate to="/hr-dashboard" replace />;
    return <Navigate to="/interview" replace />;
  }
  return children;
};

const ResultsRoute = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const canViewAsInterviewer = params.get('viewer') === 'interviewer';

  if (!isAuthenticatedUser() && !canViewAsInterviewer) {
    return <Navigate to="/login" replace />;
  }

  return <ResultsView />;
};

const HomeRoute = () => {
  if (isAuthenticatedUser()) {
    const user = getStoredUser();
    const role = user?.user_type || user?.role;
    if (role === 'BU_HEAD') return <Navigate to="/bu-dashboard" replace />;
    if (role === 'MANAGER') return <Navigate to="/manager-dashboard" replace />;
    if (role === 'INTERVIEWER') return <Navigate to="/interviewer-dashboard" replace />;
    if (role === 'HR') return <Navigate to="/hr-dashboard" replace />;
    return <Navigate to="/interview" replace />;
  }
  return <WelcomePage />;
};

const RoomSelectionRoute = () => {
  const user = getStoredUser();
  const role = user?.user_type || user?.role;
  if (role === 'BU_HEAD') return <Navigate to="/bu-dashboard" replace />;
  if (role === 'MANAGER') return <Navigate to="/manager-dashboard" replace />;
  if (role === 'INTERVIEWER') return <Navigate to="/interviewer-dashboard" replace />;
  return <RoomSelection />;
};

function App() {
  getStoredUser();

  return (
    <Router>
      <AnalyticsTracker />
      <Routes>
        {/* Welcome page - Public, first landing */}
        <Route path="/" element={<HomeRoute />} />
        <Route path="/how" element={<HowItWorksPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route
          path="/contact"
          element={
            <PublicOnlyRoute>
              <ContactPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/privacy"
          element={
            <PublicOnlyRoute>
              <PrivacyPage />
            </PublicOnlyRoute>
          }
        />

        {/* Auth pages - Public (signup & login) */}
        <Route
          path="/signup"
          element={
            <PublicOnlyRoute>
              <HRSignupPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <HRLoginPage />
            </PublicOnlyRoute>
          }
        />

        {/* Protected Routes - Only accessible after login */}
        <Route
          path="/interview"
          element={
            <AuthGuard>
              <RoomSelectionRoute />
            </AuthGuard>
          }
        />
        <Route
          path="/calendar-view"
          element={
            <AuthGuard>
              <CalendarView />
            </AuthGuard>
          }
        />
        <Route
          path="/team"
          element={
            <HRGuard>
              <TeamManagementView />
            </HRGuard>
          }
        />
        <Route
          path="/hr-dashboard"
          element={
            <HRGuard>
              <HRDashboard />
            </HRGuard>
          }
        />
        <Route
          path="/hr-dashboard/:activeTab"
          element={
            <HRGuard>
              <HRDashboard />
            </HRGuard>
          }
        />
        <Route
          path="/manager-dashboard"
          element={
            <ManagerGuard>
              <ManagerDashboard />
            </ManagerGuard>
          }
        />
        <Route
          path="/manager-dashboard/:subTab"
          element={
            <ManagerGuard>
              <ManagerDashboard />
            </ManagerGuard>
          }
        />
        <Route
          path="/bu-dashboard"
          element={
            <BUHeadGuard>
              <BUDashboard />
            </BUHeadGuard>
          }
        />
        <Route
          path="/bu-dashboard/:subTab"
          element={
            <BUHeadGuard>
              <BUDashboard />
            </BUHeadGuard>
          }
        />
        <Route
          path="/interviewer-dashboard"
          element={
            <InterviewerDashboard />
          }
        />
        <Route
            path="/results/:roomId"
          element={<ResultsRoute />}
        />
        {
            /* Note: /candidate-dashboard removed - platform is HR-only */
        }
        <Route
            path="/ai-interview/:roomId"
            element={<AIInterviewView />}
        />

        {/* Superadmin routes */}
        <Route
          path="/superadmin/login"
          element={
            <SuperadminLoginPage />
          }
        />
        <Route
          path="/superadmin"
          element={
            <SuperadminGuard>
              <SuperadminDashboard />
            </SuperadminGuard>
          }
        />

        {/* Interview Pages - Direct access allowed */}
        <Route
          path="/interview/candidate/:roomId"
          element={<CandidateViewWrapper />}
        />
        <Route
          path="/interview/interviewer/:roomId"
          element={<InterviewerViewWrapper />}
        />

        {/* Redirect unknown routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
